import { useState } from 'react';

interface Props {
    isShow: boolean;
    currentCommission?: string | number | null;
    proposedCommission?: string | number | null;
    currentServiceFee?: string | number | null;
    proposedServiceFee?: string | number | null;
}

type ChangeItem = {
    key: string;
    label: string;
    current: string;
    proposed: string;
};

const normalizeValue = (value: unknown): string => {
    if (value === null || value === undefined) {
        return '';
    }

    return String(value).trim().replace(/%$/, '');
};

const formatPercent = (value: unknown): string => {
    const normalized = normalizeValue(value);

    if (!normalized) {
        return '';
    }

    return `${normalized}%`;
};

const isDifferent = (current: unknown, proposed: unknown): boolean => {
    return normalizeValue(current) !== normalizeValue(proposed);
};

const NoChanges = () => {
    return (
        <div className="flex items-center gap-2 rounded-md bg-gray-50 px-3 py-3 text-[11px] text-gray-400">
            <svg viewBox="0 0 24 24" fill="none" className="h-4 w-4 shrink-0" aria-hidden="true" >
                <path d="m5 12.5 4.5 4L19 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>No changes</span>
        </div>
    );
};

export default function ProposedCommissionServiceFee({
    isShow,
    currentCommission,
    proposedCommission,
    currentServiceFee,
    proposedServiceFee,
}: Props) {

    const [showContent, setShowContent] = useState(true);
    const changes: ChangeItem[] = [];

    // ============================================================
    // COMMISSION RATE
    // ============================================================
    if (isDifferent(currentCommission, proposedCommission)) {
        changes.push({
            key: 'commission-rate',
            label: 'Commission Rate',
            current: formatPercent(currentCommission),
            proposed: formatPercent(proposedCommission),
        });
    }

    // ============================================================
    // SERVICE / COLLECTION FEE
    // ============================================================
    if (isDifferent(currentServiceFee, proposedServiceFee)) {
        changes.push({
            key: 'service-collection-fee',
            label: 'Service/Collection Fee',
            current: formatPercent(currentServiceFee),
            proposed: formatPercent(proposedServiceFee),
        });
    }

    if (!isShow || changes.length === 0) {
        return null;
    }

    return (
        <div className="grid grid-cols-1 gap-3.5">
            <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                {/* ================================================= */}
                {/* SECTION HEADER */}
                {/* ================================================= */}
                <button
                    type="button"
                    onClick={() => setShowContent((previous) => !previous)}
                    className="flex w-full items-center justify-between gap-3 border-b border-slate-200 bg-white px-4 py-2.5 text-left transition-colors hover:bg-slate-50"
                >
                    <div className="flex min-w-0 items-center gap-2">
                        <svg viewBox="0 0 24 24" fill="none" className={`h-3.5 w-3.5 shrink-0 text-[#186cb2] transition-transform ${showContent ? 'rotate-0' : '-rotate-90'}`} aria-hidden="true" >
                            <path
                                d="m7 10 5 5 5-5"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                        <h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-700">
                            Commission & Service Fee
                        </h3>
                        <span className="rounded-full bg-[#e3f1fa] px-1.5 py-0.5 text-[8px] font-bold text-[#186cb2] ring-1 ring-[#c7e0f3]">
                            {changes.length}
                        </span>
                    </div>
                    <span className="shrink-0 text-[9px] font-medium text-slate-400">
                        {showContent ? 'Hide' : 'Show'}
                    </span>
                </button>

                {showContent && (
                    <>
                        {/* ================================================= */}
                        {/* CURRENT / PROPOSED HEADER */}
                        {/* ================================================= */}
                        <div className="grid grid-cols-1 border-b border-slate-200 sm:grid-cols-2">
                            <div className="border-b border-slate-200 bg-slate-50 px-4 py-2.5 sm:border-b-0 sm:border-r">
                                <h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                    Current
                                </h3>
                            </div>

                            <div className="bg-[#f5f9fc] px-4 py-2.5">
                                <h3 className="text-[11px] font-semibold uppercase tracking-wide text-[#186cb2]">
                                    Proposed
                                </h3>
                            </div>
                        </div>

                        {/* ================================================= */}
                        {/* CONTENT */}
                        {/* ================================================= */}
                        <div className="grid grid-cols-1 sm:grid-cols-2">
                            {/* CURRENT */}
                            <div className="min-w-0 border-b border-slate-200 p-4 sm:border-b-0 sm:border-r">
                                {changes.length === 0 ? (
                                    <NoChanges />
                                ) : (
                                    <div className="space-y-3">
                                        {changes.map((item) => (
                                            <div key={`current-${item.key}`} className="border-b border-gray-100 pb-3 last:border-b-0 last:pb-0" >
                                                <div className="mb-1 text-[9px] font-semibold uppercase tracking-wide text-gray-400">
                                                    {item.label}
                                                </div>
                                                <div className="text-[11px] font-medium leading-relaxed text-gray-700">
                                                    {item.current || 'Not provided'}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            {/* PROPOSED */}
                            <div className="min-w-0 p-4">
                                {changes.length === 0 ? (
                                    <NoChanges />
                                ) : (
                                    <div className="space-y-3">
                                        {changes.map((item) => (
                                            <div key={`proposed-${item.key}`} className="border-b border-blue-50 pb-3 last:border-b-0 last:pb-0" >
                                                <div className="mb-1 text-[9px] font-semibold uppercase tracking-wide text-[#186cb2]">
                                                    {item.label}
                                                </div>
                                                <div className="text-[11px] font-semibold leading-relaxed text-gray-800">
                                                    {item.proposed || 'Not provided'}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}